import React, { useCallback, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import {
  Box,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DollarSign } from "lucide-react";
import CustomDatePicker from "../../common/CustomDatePicker";
import { useModalStyles } from "../../common/useModalStyles";
import { useForm } from "react-hook-form";
import dayjs, { Dayjs } from "dayjs";
import { Cancel, EditOutlined, ErrorOutlineRounded } from "@mui/icons-material";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import Button from "../../common/Button";
import { initialValuesOfProductPurchasePrice, ProductPurchasePriceInfo } from "../../../redux/slice/product/createProductSlice";
import { FieldLabel } from "../../../utils/Utility";

interface ProductCogsPopupProps {
  open: boolean;
  onClose: () => void;
  onSave: (purchasePrice: ProductPurchasePriceInfo) => void;
  purchasePriceObject: ProductPurchasePriceInfo;
  inactiveOnly: boolean;
}

const showInlineErrorMinLength = 60;

const ProductCogsPopup: React.FC<ProductCogsPopupProps> = ({
  open,
  onClose,
  onSave,
  purchasePriceObject,
  inactiveOnly,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    inputFieldSx,
    datepickerWrapSx,
    primaryBtnSx,
    cancelBtnSx,
    isDark,
  } = useModalStyles();

  const { control, handleSubmit, reset, getValues, formState } = useForm<ProductPurchasePriceInfo>({
    defaultValues: initialValuesOfProductPurchasePrice,
  });
  const { errors } = formState;

  useEffect(() => {
    if (open) {
      reset({
        ...purchasePriceObject,
        inactiveDate: purchasePriceObject.inactiveDate ? dayjs(purchasePriceObject.inactiveDate) : null,
        effectiveDate: purchasePriceObject.effectiveDate ? dayjs(purchasePriceObject.effectiveDate) : null,
      });
    }
  }, [open, purchasePriceObject, reset]);

  const validateAmount = (value: string, message: string) => {
    const val: number = value !== "" ? parseFloat(value) : 0;
    if (val === 0 || val === 0.0) {
      return message;
    }
  };

  const errorMessages = useMemo(() => {
    const errorMsgs: JSX.Element[] = [];
    if (errors) {
      const objKeys: string[] = Object.keys(errors);
      let errMsg: string | undefined;
      if (objKeys && objKeys.length > 0) {
        objKeys.forEach((key) => {
          // @ts-ignore
          errMsg = errors[key]?.message;
          if (errMsg && errMsg.length >= showInlineErrorMinLength) {
            errorMsgs.push(
              <Box key={key} sx={{ display: "flex", justifyContent: "flex-start", color: "error.main" }}>
                <Box sx={{ pr: 0.5 }}>
                  <ErrorOutlineRounded />
                </Box>
                <Box>{errMsg}</Box>
              </Box>
            );
          }
        });
      }
    }
    return errorMsgs;
  }, [errors]);

  const validateEffectiveDate = useCallback(
    (value: string | number | Date | Dayjs | null | undefined) => {
      if (!dayjs(value, "MM/DD/YYYY", true).isValid()) {
        return "Invalid date format.";
      }
      return true;
    },
    []
  );

  const validateInactiveDate = useCallback(
    (value: string | number | Date | Dayjs | null | undefined) => {
      if (!dayjs(value, "MM/DD/YYYY", true).isValid()) {
        return "Invalid date format.";
      }
      return true;
    },
    []
  );

  const validateOptionalInactiveDate = useCallback(
    (value: string | number | Date | Dayjs | null | undefined) => {
      if (
        dayjs(value, "MM/DD/YYYY", true).isValid() &&
        dayjs(getValues("effectiveDate"), "MM/DD/YYYY", true).isValid() &&
        dayjs(getValues("effectiveDate"), "MM/DD/YYYY", true).isAfter(dayjs(value, "MM/DD/YYYY", true))
      ) {
        return "The inactive date should be the same as or after the effective date.";
      }
      return true;
    },
    [getValues]
  );

  const onSubmit = useCallback((data: ProductPurchasePriceInfo) => {
    onSave({
      ...data,
      effectiveDate: data.effectiveDate ? dayjs(data.effectiveDate).format("MM/DD/YYYY") : "",
      inactiveDate: data.inactiveDate ? dayjs(data.inactiveDate).format("MM/DD/YYYY") : "",
    });
  }, [onSave]);

  return (
    <Dialog
      open={open}
      keepMounted
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      aria-describedby="product-cogs-dialog"
      onClick={(event) => event.stopPropagation()}
      PaperProps={{ sx: paperSx(isMobile) }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? "rgba(0,0,0,0.78)" : "rgba(15,23,42,0.5)",
          backdropFilter: "blur(6px)",
        },
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
        style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
      >
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <DollarSign size={22} color={tokens.accent} strokeWidth={1.8} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, pr: 4 }}>
            <Typography
              fontWeight={700}
              sx={{
                fontSize: "1.1rem",
                color: tokens.textPrimary,
                lineHeight: 1.3,
              }}
            >
              {inactiveOnly ? "Inactivate COGs" : "Add COGs"}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: tokens.textSecondary,
                mt: 0.15,
              }}
            >
              {inactiveOnly
                ? "Set an inactive date for this COGs record."
                : "Enter COGs details including effective date and purchase price."}
            </Typography>
          </Box>

          <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box sx={{ ...bodySx, minHeight: 0, overflowY: "auto" }}>
          {errorMessages.length > 0 && <Box sx={{ pb: 1 }}>{errorMessages}</Box>}

          {!inactiveOnly && (
            <Grid container spacing={2}>
              <Grid item md={6} xs={12}>
                <FieldLabel label="Effective Date" required tokens={tokens} />
                <Box sx={datepickerWrapSx(!!errors.effectiveDate)}>
                  <CustomDatePicker
                    label=""
                    openTo="day"
                    controllerName="effectiveDate"
                    control={control}
                    size="small"
                    showErrorMinLength={showInlineErrorMinLength}
                    format="MM/DD/YYYY"
                    rules={{
                      required: "Effective Date is required.",
                      validate: {
                        isValidDate: (value: string | number | Date | Dayjs | null | undefined) =>
                          validateEffectiveDate(value),
                      },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item md={6} xs={12}>
                <FieldLabel label="Inactive Date" tokens={tokens} />
                <Box sx={datepickerWrapSx(!!errors.inactiveDate)}>
                  <CustomDatePicker
                    label=""
                    openTo="day"
                    controllerName="inactiveDate"
                    control={control}
                    size="small"
                    showErrorMinLength={showInlineErrorMinLength}
                    format="MM/DD/YYYY"
                    rules={{
                      validate: {
                        isValidDate: (value: string | number | Date | Dayjs | null | undefined) =>
                          validateOptionalInactiveDate(value),
                      },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FieldLabel label="Purchase Price" required tokens={tokens} />
                <Box sx={inputFieldSx(!!errors.purchasePrice)}>
                  <CustomCurrencyField
                    control={control}
                    controllerName="purchasePrice"
                    label=""
                    size="small"
                    showErrorMinLength={showInlineErrorMinLength}
                    rules={{
                      required: "Purchase Price is required.",
                      validate: {
                        validateAmount: (value: string) =>
                          validateAmount(value, "Enter a valid Purchase Price."),
                      },
                    }}
                    sx={{
                      "& .MuiInputAdornment-root": { color: tokens.textSecondary },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          )}

          {inactiveOnly && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FieldLabel label="Inactive Date" required tokens={tokens} />
                <Box sx={datepickerWrapSx(!!errors.inactiveDate)}>
                  <CustomDatePicker
                    label=""
                    openTo="day"
                    controllerName="inactiveDate"
                    control={control}
                    size="small"
                    showErrorMinLength={showInlineErrorMinLength}
                    format="MM/DD/YYYY"
                    rules={{
                      required: "Inactive Date is required.",
                      validate: {
                        isValidDate: (value: string | number | Date | Dayjs | null | undefined) =>
                          validateInactiveDate(value),
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>

        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <Button
              type="button"
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              startIcon={<EditOutlined sx={{ fontSize: "1rem !important" }} />}
              sx={primaryBtnSx}
            >
              {inactiveOnly ? "Inactivate" : "Add COGs"}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={onClose}
              startIcon={<Cancel sx={{ fontSize: "1rem !important" }} />}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default ProductCogsPopup;
